import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  fetchCreatorDashboard,
  fetchStudioNotifications,
  transitionWorkflow,
  WORKFLOW_STATUSES,
} from "./contentStudioApi";
import { runtimeConfig } from "../../config/runtimeConfig";
import { fetchStudioCapabilities } from "../Helper/studioCapabilities";
import CommunityCreatePostCard from "../Community/CommunityCreatePostCard";
import { fetchSocialProfile } from "../Community/communitySocialApi";
import DonationModeSettings from "./DonationModeSettings";
import RecipientPayoutSettings from "../Workspace/RecipientPayoutSettings";
import "../Community/CommunitySocial.css";
import "./ContentStudio.css";

function adminVideosCreateUrl(creatorId) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "") + "/videos/create";
  if (!creatorId) {
    return base;
  }
  return base + "?creator_id=" + encodeURIComponent(creatorId);
}

function adminVideosEditUrl(adminVideoId) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "");
  return base + "/videos/edit/?admin_video_id=" + encodeURIComponent(adminVideoId);
}

function adminCreatorEditUrl(creatorId) {
  const base = (runtimeConfig.adminBaseUrl || "").replace(/\/+$/, "");
  return base + "/creators/edit?creator_id=" + encodeURIComponent(creatorId);
}

// Creator edit / video tools live in Blade Admin; staff moderators also carry
// is_platform_admin, so require the server-issued administrator / sub_admin portal as well.
function canUseAdminTools(caps) {
  const portals = caps && Array.isArray(caps.portals) ? caps.portals : [];
  return (
    !!(caps && caps.is_platform_admin) &&
    portals.some((p) => ["administrator", "sub_admin"].includes(String(p.key || p.role)))
  );
}

function CreatorStudioPage(props) {
  const embedded = !!props.embedded;
  const routeCreatorId =
    (props.match && props.match.params && props.match.params.creatorId) ||
    props.creatorId ||
    (typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("creator_id")) ||
    "";

  const [creatorId, setCreatorId] = useState(routeCreatorId);
  const [resolving, setResolving] = useState(!routeCreatorId);
  const [dash, setDash] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [postNote, setPostNote] = useState("");
  const [adminTools, setAdminTools] = useState(false);
  const [sectionInternal, setSectionInternal] = useState("dashboard");
  const section =
    props.section != null ? props.section : sectionInternal;
  const setSection =
    typeof props.onSectionChange === "function"
      ? props.onSectionChange
      : setSectionInternal;

  useEffect(() => {
    let cancelled = false;
    fetchStudioCapabilities().then((caps) => {
      if (!cancelled) setAdminTools(canUseAdminTools(caps));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (routeCreatorId) {
      setCreatorId(routeCreatorId);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    fetchStudioCapabilities().then((caps) => {
      if (cancelled) return;
      const first =
        caps && caps.creator_ids && caps.creator_ids.length
          ? String(caps.creator_ids[0])
          : "";
      if (first && props.history && !embedded) {
        props.history.replace("/studio/creator/" + first);
        return;
      }
      if (first && embedded) {
        setCreatorId(first);
        setResolving(false);
        return;
      }
      setCreatorId("");
      setResolving(false);
      setError(
        first
          ? ""
          : "No creator workspace linked to this account. Open Media Library or ask an admin to assign a creator."
      );
    });
    return () => {
      cancelled = true;
    };
  }, [routeCreatorId]);

  const load = async () => {
    if (!creatorId) {
      return;
    }
    setError("");
    const body = await fetchCreatorDashboard(creatorId);
    if (!body.success) {
      setError(body.error || "Unable to load creator studio");
      setDash(null);
      return;
    }
    setDash(body);
    const n = await fetchStudioNotifications({ creator_id: creatorId });
    setNotes(n);
  };

  useEffect(() => {
    if (!creatorId || resolving) return;
    load();
    fetchSocialProfile().then((res) => setProfile(res.profile));
  }, [creatorId, resolving]);

  if (resolving) {
    return (
      <div className="cstudio">
        <p className="cstudio-muted">Resolving creator workspace…</p>
      </div>
    );
  }

  const onTransition = async (adminVideoId, status) => {
    setBusy(true);
    await transitionWorkflow({
      admin_video_id: adminVideoId,
      status: status,
      creator_id: creatorId,
    });
    await load();
    setBusy(false);
  };

  const counts = (dash && dash.counts) || {};
  const by = counts.by_workflow || {};
  const creator = (dash && dash.creator) || {};
  const analytics = (dash && dash.analytics) || {};
  const modules = (dash && dash.modules) || [];

  const navItems = [
    { key: "dashboard", label: "Dashboard" },
    { key: "profile", label: "Profile & Branding" },
    { key: "media", label: "Media & Publish" },
    { key: "community", label: "Community" },
    { key: "analytics", label: "Analytics" },
    { key: "integrations", label: "Modules" },
    { key: "notifications", label: "Notifications" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className={embedded ? "cstudio cstudio--embedded" : "cstudio"}>
      {!embedded ? (
        <Helmet>
          <title>
            {(creator.display_name || "Creator") + " · Creator Studio"}
          </title>
        </Helmet>
      ) : null}
      {!embedded ? (
        <header className="cstudio__header">
          <div>
            <p className="cstudio-eyebrow">Creator Studio</p>
            <h1>{creator.display_name || "Creator"}</h1>
            {creator.slug ? (
              <p className="cstudio-muted">@{creator.slug}</p>
            ) : null}
          </div>
          <nav className="cstudio__nav">
            <Link to={"/studio/library?creator_id=" + encodeURIComponent(creatorId)}>
              Media Library
            </Link>
            <Link to="/studio/timeline">Timeline</Link>
            <Link to="/guide">Guide</Link>
            {adminTools ? (
              <a href={adminVideosCreateUrl(creatorId)} target="_blank" rel="noopener noreferrer">
                Upload video
              </a>
            ) : null}
          </nav>
        </header>
      ) : null}

      {error && <p className="cstudio-error">{error}</p>}

      {!embedded ? (
        <nav className="cstudio-tabs" aria-label="Creator studio sections">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={
                "cstudio-tab" + (section === item.key ? " cstudio-tab--active" : "")
              }
              onClick={() => setSection(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      ) : null}

      {section === "dashboard" && (
        <>
          <div className="cstudio-grid">
            <div className="cstudio-stat">
              <strong>{counts.total || 0}</strong>
              <span>Media assets</span>
            </div>
            <div className="cstudio-stat">
              <strong>{by.draft || 0}</strong>
              <span>Draft</span>
            </div>
            <div className="cstudio-stat">
              <strong>{by.in_review || 0}</strong>
              <span>In review</span>
            </div>
            <div className="cstudio-stat">
              <strong>{by.published || 0}</strong>
              <span>Published</span>
            </div>
            <div className="cstudio-stat">
              <strong>{counts.scheduled_broadcasts || 0}</strong>
              <span>Channel schedules</span>
            </div>
            <div className="cstudio-stat">
              <strong>{analytics.content_actions || 0}</strong>
              <span>Content actions (24h)</span>
            </div>
          </div>

          {!(counts.total > 0) && (
            <section className="cstudio-panel cstudio-empty">
              <h2>Get started</h2>
              <p className="cstudio-muted">
                No media yet. Upload a video, publish to Community, or open the Media Library.
              </p>
              <div className="cstudio-toolbar">
                {adminTools ? (
                  <a
                    className="cstudio-btn"
                    href={adminVideosCreateUrl(creatorId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Upload video
                  </a>
                ) : null}
                <Link
                  className="cstudio-btn cstudio-btn--ghost"
                  to={"/studio/library?creator_id=" + encodeURIComponent(creatorId)}
                >
                  Open library
                </Link>
                <button
                  type="button"
                  className="cstudio-btn cstudio-btn--ghost"
                  onClick={() => setSection("community")}
                >
                  Post to Community
                </button>
              </div>
            </section>
          )}

          <section className="cstudio-panel">
            <h2>Quick modules</h2>
            <div className="cstudio-module-grid">
              {modules.map((m) => (
                <Link key={m.key} className="cstudio-module-card" to={m.path}>
                  <strong>{m.label}</strong>
                  <span className="cstudio-muted">Open</span>
                </Link>
              ))}
              <button
                type="button"
                className="cstudio-module-card"
                onClick={() => setSection("storefront")}
              >
                <strong>Shop &amp; revenue</strong>
                <span className="cstudio-muted">Storefront</span>
              </button>
            </div>
          </section>
        </>
      )}

      {section === "profile" && (
        <section className="cstudio-panel">
          <h2>Profile &amp; branding</h2>
          <div className="cstudio-profile">
            {creator.banner ? (
              <img className="cstudio-banner" src={creator.banner} alt="" />
            ) : (
              <div className="cstudio-banner cstudio-banner--empty">No banner yet</div>
            )}
            <div className="cstudio-profile__row">
              {creator.avatar ? (
                <img className="cstudio-avatar" src={creator.avatar} alt="" />
              ) : (
                <div className="cstudio-avatar cstudio-avatar--empty">?</div>
              )}
              <div>
                <h3>{creator.display_name || "Untitled creator"}</h3>
                <p className="cstudio-muted">
                  {creator.is_verified ? "Verified · " : ""}
                  {creator.visibility || "public"} · {creator.status || "—"}
                </p>
              </div>
            </div>
            <p>{creator.biography || "No biography yet. Add one in Creator settings."}</p>
            <ul className="cstudio-list cstudio-list--plain">
              <li>
                <span className="cstudio-muted">Email</span>
                <strong>{creator.contact_email || "—"}</strong>
              </li>
              <li>
                <span className="cstudio-muted">Website</span>
                <strong>{creator.website || "—"}</strong>
              </li>
              <li>
                <span className="cstudio-muted">Social</span>
                <strong>
                  {creator.social_links && Object.keys(creator.social_links).length
                    ? Object.keys(creator.social_links).join(", ")
                    : "—"}
                </strong>
              </li>
            </ul>
            {adminTools ? (
              <a
                className="cstudio-btn"
                href={adminCreatorEditUrl(creatorId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit profile
              </a>
            ) : null}
          </div>
        </section>
      )}

      {(section === "donations" || section === "settings") && (
        <DonationModeSettings
          targetType="creator"
          targetId={creatorId}
          initialMode={creator.donation_mode}
          initialUrl={creator.donation_url || ""}
          onSaved={() => load()}
        />
      )}

      {section === "settings" && creatorId ? (
        <RecipientPayoutSettings
          payableType="creator"
          payableId={creatorId}
          workspace="creator"
        />
      ) : null}

      {section === "media" && (
        <section className="cstudio-panel">
          <h2>Media lifecycle</h2>
          <div className="cstudio-toolbar" style={{ marginBottom: "0.75rem" }}>
            {adminTools ? (
              <a
                className="cstudio-btn"
                href={adminVideosCreateUrl(creatorId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Upload Video
              </a>
            ) : null}
            <Link
              className="cstudio-btn cstudio-btn--ghost"
              to={"/studio/library?creator_id=" + encodeURIComponent(creatorId)}
            >
              Full media library
            </Link>
            <Link className="cstudio-btn cstudio-btn--ghost" to="/studio/timeline">
              Timeline studio
            </Link>
          </div>
          <ul className="cstudio-list">
            {((dash && dash.recent) || []).map((item) => (
              <li key={item.media_asset_key || item.admin_video_id}>
                <div>
                  <strong>{item.title}</strong>
                  <div>
                    <span
                      className={
                        "cstudio-badge cstudio-badge--" +
                        ((item.workflow && item.workflow.status) || "draft")
                      }
                    >
                      {(item.workflow && item.workflow.status) || "draft"}
                    </span>{" "}
                    <span className="cstudio-muted">
                      {(item.ownership && item.ownership.origin) || "creator"}
                    </span>
                  </div>
                  {adminTools && item.admin_video_id ? (
                    <a
                      href={adminVideosEditUrl(item.admin_video_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cstudio-muted"
                    >
                      Edit video
                    </a>
                  ) : null}
                </div>
                <div className="cstudio-workflow">
                  {WORKFLOW_STATUSES.slice(0, 6).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="cstudio-btn cstudio-btn--ghost"
                      disabled={busy}
                      onClick={() => onTransition(item.admin_video_id, s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </li>
            ))}
            {!(dash && dash.recent && dash.recent.length) && (
              <li className="cstudio-muted">No media yet for this creator.</li>
            )}
          </ul>
        </section>
      )}

      {section === "community" && (
        <section className="cstudio-panel">
          <h2>Publish to Community</h2>
          <p className="cstudio-muted">
            Posts are published as this creator — not as the signed-in manager.
          </p>
          <CommunityCreatePostCard
            profile={profile}
            onProfileUpdate={setProfile}
            creatorId={creatorId ? Number(creatorId) : null}
            entityName={
              (dash && dash.creator && (dash.creator.display_name || dash.creator.name)) ||
              null
            }
            entityAvatar={(dash && dash.creator && dash.creator.avatar) || null}
            entityPath={
              dash && dash.creator && dash.creator.slug
                ? "/creator/" + dash.creator.slug
                : null
            }
            compact
            onPosted={(post, meta) => {
              if (meta && meta.optimistic) return;
              if (meta && meta.rollback) {
                setPostNote("Post failed — try again.");
                return;
              }
              setPostNote("Published to Community feed.");
            }}
          />
          {postNote ? <p className="cstudio-muted">{postNote}</p> : null}
          {!embedded ? (
            <p className="cstudio-muted" style={{ marginTop: "1rem" }}>
              {(dash && dash.deferred && dash.deferred.post_scheduling) ||
                "Community post scheduling is deferred."}
            </p>
          ) : null}
        </section>
      )}

      {section === "analytics" && (
        <section className="cstudio-panel">
          <h2>Analytics (24h)</h2>
          <div className="cstudio-grid">
            <div className="cstudio-stat">
              <strong>{analytics.broadcast_events || 0}</strong>
              <span>Broadcast events</span>
            </div>
            <div className="cstudio-stat">
              <strong>{analytics.content_actions || 0}</strong>
              <span>Content actions</span>
            </div>
            <div className="cstudio-stat">
              <strong>{analytics.channel_links || 0}</strong>
              <span>Channel asset links</span>
            </div>
          </div>
          {!analytics.content_actions && !analytics.broadcast_events ? (
            <p className="cstudio-muted">
              No activity in the last 24 hours. Publish media or run a broadcast to see stats.
            </p>
          ) : null}
          <Link className="cstudio-btn cstudio-btn--ghost" to="/ops">
            Open Live Ops
          </Link>
        </section>
      )}

      {section === "integrations" && (
        <section className="cstudio-panel">
          <h2>Cross-module integrations</h2>
          <div className="cstudio-module-grid">
            <Link
              className="cstudio-module-card"
              to={"/studio/library?creator_id=" + encodeURIComponent(creatorId)}
            >
              <strong>Media</strong>
              <span className="cstudio-muted">Library &amp; workflow</span>
            </Link>
            <Link className="cstudio-module-card" to="/studio/timeline">
              <strong>Scheduler</strong>
              <span className="cstudio-muted">Timeline studio</span>
            </Link>
            <Link className="cstudio-module-card" to="/studio/advertising">
              <strong>Advertising</strong>
              <span className="cstudio-muted">Campaigns</span>
            </Link>
            <Link className="cstudio-module-card" to="/community/social">
              <strong>Community</strong>
              <span className="cstudio-muted">Social feed</span>
            </Link>
            <button
              type="button"
              className="cstudio-module-card"
              onClick={() => setSection("storefront")}
            >
              <strong>Shop</strong>
              <span className="cstudio-muted">Products &amp; orders</span>
            </button>
            <Link className="cstudio-module-card" to="/ops">
              <strong>Livestream / Ops</strong>
              <span className="cstudio-muted">Control room</span>
            </Link>
          </div>
        </section>
      )}

      {section === "notifications" && (
        <section className="cstudio-panel">
          <h2>Notifications</h2>
          <ul className="cstudio-list">
            {notes.slice(0, 20).map((n) => (
              <li key={n.id}>
                <div>
                  <strong>{n.title}</strong>
                  <div className="cstudio-muted">{n.message}</div>
                </div>
                <span className="cstudio-badge">{n.event_type}</span>
              </li>
            ))}
            {!notes.length && (
              <li className="cstudio-muted">No notifications yet — workflow events appear here.</li>
            )}
          </ul>
        </section>
      )}

      {section === "settings" && (
        <section className="cstudio-panel">
          <h2>Account Settings</h2>
          <p className="cstudio-muted">
            Profile, branding, and verification open from Creator Page settings.
            Password and account security use your platform login. Shop financials open in
            the Seller Dashboard.
          </p>
          <div className="cstudio-toolbar">
            <button
              type="button"
              className="cstudio-btn"
              onClick={() => setSection("profile")}
            >
              Profile &amp; personal info
            </button>
            <button
              type="button"
              className="cstudio-btn cstudio-btn--ghost"
              onClick={() => setSection("notifications")}
            >
              Notifications
            </button>
            {adminTools ? (
              <a
                className="cstudio-btn cstudio-btn--ghost"
                href={adminCreatorEditUrl(creatorId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Creator settings
              </a>
            ) : null}
            <button
              type="button"
              className="cstudio-btn cstudio-btn--ghost"
              onClick={() => setSection("storefront")}
            >
              Revenue &amp; shop
            </button>
            <Link className="cstudio-btn cstudio-btn--ghost" to="/account">
              Password &amp; security
            </Link>
          </div>
          {dash && dash.storefront ? (
            <p className="cstudio-muted" style={{ marginTop: "1rem" }}>
              Storefront: {dash.storefront.name || "#" + dash.storefront.id} (
              {dash.storefront.status || "—"})
            </p>
          ) : (
            <p className="cstudio-muted" style={{ marginTop: "1rem" }}>
              No storefront linked yet.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

export default CreatorStudioPage;
